import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setIsOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur-md"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="flex w-full items-center gap-4 px-4 py-3 md:px-8 md:py-4">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-fill text-lg leading-none" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-wide text-foreground-950">
              蜜桃<span className="text-primary-600">33</span>
            </span>
            <span className="mt-1 text-[9px] uppercase tracking-[0.28em] text-foreground-400">
              Movie Library
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `relative rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-primary-600"
                    : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span className="absolute -bottom-0.5 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-primary-500" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto hidden items-center gap-3 lg:flex">
          <form onSubmit={submitSearch} className="relative">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              aria-label="搜索影视"
              placeholder="搜索片名、类型、演员"
              className="h-9 w-52 rounded-md border border-background-200 bg-background-100/70 pl-9 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-300 focus:bg-background-50 focus:outline-none lg:w-64"
            />
          </form>
          <span className="hidden rounded-full border border-accent-200 bg-accent-50 px-3 py-1.5 text-[11px] whitespace-nowrap text-accent-700 xl:inline-flex">
            演示站点
          </span>
        </div>

        <button
          type="button"
          aria-label="打开导航菜单"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-800 lg:hidden"
        >
          <i className={isOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {isOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
          <form onSubmit={submitSearch} className="relative mb-4">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              aria-label="搜索影视"
              placeholder="搜索片名、类型、演员"
              className="h-11 w-full rounded-md border border-background-200 bg-background-100 pl-9 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
            />
          </form>
          <div className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                    isActive
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-base opacity-50" />
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}